// TODO(jvoll, LP-4515): This file is way TOO LONG! Split into multiple
// utilities files.
import {t} from "@lingui/core/macro";
import memoize from "lodash.memoize";
import moment from "moment";

import {
    SUBJECT_CHALLENGE_QUIZ_TYPE,
    type Attempt,
    type ContentItem,
    type Criterion,
    type Exercise,
    type InterspersedQuizT,
    type LiveUpdateDetails,
    type MasteryLevel,
    type MasteryMap,
    type MasteryPoints,
    type QuizTutorial,
    type QuizType,
    type Score,
} from "@khan/content-types";
import {
    constants,
    getLevelChange,
    getMasteryDisplay,
    masteryValueConstants,
    masteryValues,
} from "@khan/mastery";

export type MasteryLevelDefinition = {
    id: string;
    title: string;
    start: number;
    end: number;
};

export type MasteryLevelDetails = {
    inProgressLevel: MasteryLevel;
    inProgressLeveledUp: boolean;
    nextLevelToAttain: MasteryLevel;
    goalToAttainNextLevel: number;
};

export const colors = {
    coral: "#ff714d",
} as const;

export const getLevelIndex = (level: MasteryLevel): number =>
    masteryValues.indexOf(level);

export const HAS_FULLY_MASTERED_STATE:
    | "attempted"
    | "familiar"
    | "mastered"
    | "proficient"
    | "unfamiliar" = masteryValues[masteryValues.length - 1];

type Comparer<T> = {
    lessThan: (a: T, b: T) => boolean;
    lessThanOrEqual: (a: T, b: T) => boolean;
    greaterThan: (a: T, b: T) => boolean;
    greaterThanOrEqual: (a: T, b: T) => boolean;
    equal: (a: T, b: T) => boolean;
};

// TODO(kevinb): move this to a more general package
function makeComparer<T>(cmpFn: (a: T, b: T) => number): Comparer<T> {
    return {
        lessThan: (a: T, b: T) => cmpFn(a, b) < 0,
        lessThanOrEqual: (a: T, b: T) => cmpFn(a, b) <= 0,
        greaterThan: (a: T, b: T) => cmpFn(a, b) > 0,
        greaterThanOrEqual: (a: T, b: T) => cmpFn(a, b) >= 0,
        equal: (a: T, b: T) => cmpFn(a, b) === 0,
    };
}

export const masteryLevelComparer: Comparer<MasteryLevel> = makeComparer(
    (a: MasteryLevel, b: MasteryLevel) => getLevelIndex(a) - getLevelIndex(b),
);

/**
 * A quiz is complete if all its exercises are proficient or mastered; a test
 * is complete if all exercises are mastered.
 */
export const QUIZ_COMPLETE_LEVELS = [
    constants.PROFICIENT,
    constants.MASTERED,
] as const;
export const TEST_COMPLETE_LEVELS = [constants.MASTERED] as const;
export const COURSE_CHALLENGE_COMPLETE_SCORE = 90;

export const masteryDisplayColors: Record<MasteryLevel, string> = {
    [constants.UNFAMILIAR]: "#F7F8FA",
    [constants.ATTEMPTED]: "#C66024",
    [constants.FAMILIAR]: "#D77D0F",
    [constants.PROFICIENT]: "#948EBE",
    [constants.MASTERED]: "#594094",
};

export const exerciseMasteryThresholds: {
    attempted: number;
    familiar: number;
    mastered: number;
    proficient: number;
    unfamiliar: number;
} = {
    [constants.UNFAMILIAR]: 0.0,
    [constants.ATTEMPTED]: 0.0,
    [constants.FAMILIAR]: 0.7,
    [constants.PROFICIENT]: 1.0,
    [constants.MASTERED]: 1.0,
};

export const unitMasteryPercentage = 0.9;

export const getMasteryLevelDefinitions = memoize(
    (): Array<MasteryLevelDefinition> => [
        {
            id: constants.LEVEL1,
            title: t`Level 1`,
            start: 0,
            end: 0.25,
        },
        {
            id: constants.LEVEL2,
            title: t`Level 2`,
            start: 0.25,
            end: 0.5,
        },
        {
            id: constants.LEVEL3,
            title: t`Level 3`,
            start: 0.5,
            end: 0.75,
        },
        {
            id: constants.LEVEL4,
            title: t`Level 4`,
            start: 0.75,
            end: unitMasteryPercentage,
        },
        {
            id: constants.LEVEL5,
            title: t`Level 5 (Mastered)`,
            start: unitMasteryPercentage,
            end: 1.0,
        },
        {
            id: constants.LEVEL6,
            title: t`Level 6`,
            start: 1.0,
            end: 1.0,
        },
    ],
);

export const levelPointsMap = {
    unfamiliar: 0,
    attempted: 0,
    familiar: 50,
    proficient: 80,
    mastered: 100,
} as const;

export function getMasteryLevel(
    points: number,
    totalPoints: number,
): MasteryLevelDefinition {
    const masteryLevelDefinitions = getMasteryLevelDefinitions();
    const level = [...masteryLevelDefinitions].reverse().find((definition) => {
        const pointsAtStart = totalPoints * definition.start;
        return points >= pointsAtStart;
    });

    return level || masteryLevelDefinitions[0];
}

// TODO(jvoll): it would be awesome if we could either clearly differentiate
// (rename) or combine the MasteryLevel and MasteryLevelDefinition types.
export const getMasteryLevelForExerciseFromMap = (
    exercise: Readonly<Exercise>,
    map: Readonly<MasteryMap>,
): MasteryLevel => {
    return map[`e${exercise.contentId}`] || constants.UNFAMILIAR;
};

export function getNextUnitMasteryLevel(
    points: number,
    totalPoints: number,
): MasteryLevelDefinition {
    const currentLevel = getMasteryLevel(points, totalPoints);
    const masteryLevelDefinitions = getMasteryLevelDefinitions();
    const currentIndex = masteryLevelDefinitions.findIndex(
        (level) => level.id === currentLevel.id,
    );

    return (
        masteryLevelDefinitions[currentIndex + 1] ||
        masteryLevelDefinitions[masteryLevelDefinitions.length - 1]
    );
}

/**
 * Returns the number of questions in a set that must be completed correctly
 * for a learner to advance to the next mastery level.
 */
export function getGoalForCount(count: number, level: MasteryLevel): number {
    const isUnfamiliarOrAttempted =
        level === constants.UNFAMILIAR || level === constants.ATTEMPTED;

    return isUnfamiliarOrAttempted
        ? Math.ceil(count * exerciseMasteryThresholds[constants.FAMILIAR])
        : count;
}

export const getNextMasteryLevel = (level: MasteryLevel): MasteryLevel => {
    const index = masteryValues.indexOf(level);
    const nextMasteryLevel = nextLevelToAspireTo(level, index);
    const lastMasteryLevel = masteryValues[masteryValues.length - 1];

    return nextMasteryLevel || lastMasteryLevel;
};

/**
 * Returns the in-progress mastery level based on the current score.  If we've
 * already reached Proficient or Mastered, then we remain the same level.
 * Otherwise, figure out what the new level is.  Users can *never* stay
 * "unfamiliar" AKA "not started".
 */
export const getInProgressMasteryLevel = (
    currentLevel: MasteryLevel,
    numCorrect: number,
    totalNumberOfQuestions: number,
): MasteryLevel => {
    const staySameLevel =
        currentLevel === constants.PROFICIENT ||
        currentLevel === constants.MASTERED;

    if (staySameLevel) {
        return currentLevel;
    }

    const score = numCorrect / totalNumberOfQuestions;
    const leveledUpToProficient =
        score >= exerciseMasteryThresholds[constants.PROFICIENT];

    if (leveledUpToProficient) {
        return constants.PROFICIENT;
    }

    const isFamiliar =
        currentLevel === constants.FAMILIAR ||
        score >= exerciseMasteryThresholds[constants.FAMILIAR];

    if (isFamiliar) {
        return constants.FAMILIAR;
    }

    return constants.ATTEMPTED;
};

// This is the level we want the user to aspire to.  The next level from
// "unfamiliar" AKA "not started" is "attempted", but we don't want to suggest
// to our users that they should aspire to merely attempt or struggle, we want
// them to reach familiar.
const nextLevelToAspireTo = (
    level: MasteryLevel,
    index: number,
): MasteryLevel => {
    const jumpNLevels = level === constants.UNFAMILIAR ? 2 : 1;
    return masteryValues[index + jumpNLevels];
};

/**
 * Returns the next mastery level for a current mastery level
 */
export const getNextMasteryLevelDetails = (
    currentLevel: MasteryLevel,
    numCorrect: number,
    totalQuestions: number,
): MasteryLevelDetails => {
    if (currentLevel === constants.MASTERED) {
        return {
            inProgressLevel: currentLevel,
            inProgressLeveledUp: false,
            nextLevelToAttain: currentLevel,
            // TODO (jsatk): Do we need to do the calculation below for "mastered"?
            goalToAttainNextLevel: Math.ceil(
                exerciseMasteryThresholds[currentLevel] * totalQuestions,
            ),
        };
    }
    const inprogressLevel = getInProgressMasteryLevel(
        currentLevel,
        numCorrect,
        totalQuestions,
    );
    const previousInprogressLevel = getInProgressMasteryLevel(
        currentLevel,
        numCorrect - 1,
        totalQuestions,
    );
    const adjustedIndex = masteryValues.indexOf(inprogressLevel);
    const nextLevel = nextLevelToAspireTo(inprogressLevel, adjustedIndex);

    return {
        inProgressLevel: inprogressLevel,
        inProgressLeveledUp: previousInprogressLevel !== inprogressLevel,
        nextLevelToAttain: nextLevel,
        goalToAttainNextLevel: Math.ceil(
            exerciseMasteryThresholds[nextLevel] * totalQuestions,
        ),
    };
};

/**
 * Return the full number of items required unless the user has gotten
 * the first x in a row correct for a type of completion criterion.
 */
export function getIndicatorCount(
    attempts: Array<Attempt>,
    criterion: Criterion,
): number {
    if (criterion.type === "first_x_or_num_correct_in_a_row") {
        if (criterion.first < criterion.numRequired) {
            if (attempts.length === criterion.first) {
                if (attempts.every((i) => i.correct)) {
                    return criterion.first;
                }
            }
        }
    }

    return criterion.numRequired;
}

/**
 * Calculates a percentage rounded to a whole number.
 *
 * returns 0 if denominator < 1
 * returns 100 if numerator/denominator > 1
 */
export const getRoundedPercent = (
    numerator: number,
    denominator: number,
): number => {
    if (denominator < 1) {
        return 0;
    }

    return Math.min(100, Math.round(100 * (numerator / denominator)));
};

/**
 * Calculates the percentage correct the user scored rounded to a whole number.
 *
 * returns 0 if score.numAttempted < 1
 * returns 100 if score.numCorrect > score.numAttempted
 */
export const getPercentCorrect = (score: Score): number =>
    getRoundedPercent(score.numCorrect, score.numAttempted);

/**
 * Returns the user's best score for a given quiz/unit-test.
 *
 * If `moment` is available, this will return the percentage and timestamp as
 * strings; otherwise it will return the percentage and a null timestamp.
 */
export const getScoreAndDateText = (
    score: Score,
): {
    dateText: string | null;
    scoreText: string;
} => {
    const percent = getPercentCorrect(score);
    const date = moment(score.completedDate).fromNow();

    return {
        scoreText: `${percent}%`,
        dateText: date,
    };
};

/**
 * Returns true if the user has reached mastery (level 5 or 6).
 *
 * Obtaining 100% of available mastery points is not necessary to be have
 * mastered a unit/course.
 */
export const achievedMastery = (
    points: number,
    availablePoints: number,
): boolean => {
    if (availablePoints < 1) {
        return false;
    }
    const level = getMasteryLevel(points, availablePoints);
    return level.id === constants.LEVEL5 || level.id === constants.LEVEL6;
};

/**
 * Return an array of mastery levels based on the exercises in a quiz and a
 * current MasteryMap.
 */
export const getMasteryLevelsForQuizOrTest = (
    quiz: InterspersedQuizT,
    masteryMap: MasteryMap,
): Array<MasteryLevel> => {
    const contentItemsForQuiz = (quiz.tutorials || []).reduce<Array<any>>(
        (allContentItems, tutorial) => [
            ...allContentItems,
            ...tutorial.contentItems,
        ],
        [],
    );

    return contentItemsForQuiz
        .filter((c) => c.kind === "Exercise")
        .map((c) => masteryMap["e" + c.contentId] || constants.UNFAMILIAR);
};

/**
 * Return the lowest level from the passed-in array. If the lowest level is
 * `proficient`, for example, it means all levels are proficient or above.
 *
 * The accumulator starts at the maximum level, "mastered," and goes down from
 * there. However, if the input array is empty, we return that the minimum
 * level is "unfamiliar."
 **/
export const getMinimumMasteryLevel = (
    levels: Array<MasteryLevel>,
): MasteryLevel =>
    levels.length > 0
        ? levels.reduce((acc, level) => {
              const currentMinimum = levelPointsMap[acc];
              const potentialNewMinimum = levelPointsMap[level];
              return potentialNewMinimum < currentMinimum ? level : acc;
          }, constants.MASTERED)
        : constants.UNFAMILIAR;

/**
 * Return the lowest level of mastery for any skill covered by a quiz or test.
 */
export const getMinimumMasteryLevelForQuizOrTest = (
    quiz: InterspersedQuizT,
    masteryMap: MasteryMap,
): MasteryLevel =>
    getMinimumMasteryLevel(getMasteryLevelsForQuizOrTest(quiz, masteryMap));

export const areAllExercisesProficientOrAboveForQuiz = (
    quiz: InterspersedQuizT,
    masteryMap: MasteryMap,
): boolean => {
    const minimumLevel = getMinimumMasteryLevelForQuizOrTest(quiz, masteryMap);
    return (
        minimumLevel === constants.PROFICIENT ||
        minimumLevel === constants.MASTERED
    );
};

export const isCourseChallengeType = (type: QuizType): boolean =>
    type === SUBJECT_CHALLENGE_QUIZ_TYPE;

export const isCourseChallenge = ({type}: InterspersedQuizT): boolean =>
    isCourseChallengeType(type);

export const isCourseChallengeComplete = (score?: Score): boolean =>
    !!score && getPercentCorrect(score) >= COURSE_CHALLENGE_COMPLETE_SCORE;

const isCorrect = ({correct}: Attempt): boolean => !!correct;
const isIncorrect = ({correct}: Attempt): boolean => !correct;
export const getNumCorrect = (attempts: Array<Attempt>): number =>
    attempts.filter(isCorrect).length;
export const getNumIncorrect = (attempts: Array<Attempt>): number =>
    attempts.filter(isIncorrect).length;

export const getLiveMasteryDetails = (
    attempts: Array<Attempt>,
    criterion: Criterion,
    currentFpmLevel: MasteryLevel,
): LiveUpdateDetails => {
    const total = getIndicatorCount(attempts, criterion);

    const numCorrect = getNumCorrect(attempts);
    const numIncorrect = getNumIncorrect(attempts);
    const next = getNextMasteryLevelDetails(currentFpmLevel, numCorrect, total);

    let showLiveUpdates = true;
    let text = "";
    let levelledUp = false;

    // If there is no way to reach the goal, don't show the mastery levels
    if (
        next.nextLevelToAttain === constants.MASTERED ||
        next.inProgressLevel === constants.PROFICIENT
    ) {
        text = t`Skill is ${getMasteryDisplay(next.inProgressLevel)}!`;
    } else if (
        next.inProgressLeveledUp &&
        next.inProgressLevel === constants.FAMILIAR &&
        currentFpmLevel === constants.UNFAMILIAR
    ) {
        text = t`Leveled up to ${getMasteryDisplay(constants.FAMILIAR)}!`;

        levelledUp = true;
    } else if (total - numIncorrect < next.goalToAttainNextLevel) {
        showLiveUpdates = false;
    } else {
        text = t`Get ${next.goalToAttainNextLevel} of ${total} questions to level up to ${getMasteryDisplay(next.nextLevelToAttain)}`;
    }

    return {showLiveUpdates, text, levelledUp};
};

const getMasteryMapFromContentItems = (
    contentItems: Array<ContentItem>,
): MasteryMap =>
    contentItems.reduce<Record<string, any>>(
        (acc, item) =>
            item.kind === "Exercise"
                ? Object.assign(acc, {[item.progressKey]: constants.UNFAMILIAR})
                : acc,
        {},
    );

// Method used in fallback cases where we do not have the current mastery level
// coming from the back-end.
export const getNewLevel = (
    correct: number,
    total: number,
    percent: number,
): MasteryLevel => {
    const familiarThreshold = 0.7;

    if (correct === total) {
        return constants.PROFICIENT;
    }

    if (percent > familiarThreshold) {
        return constants.FAMILIAR;
    }

    return constants.ATTEMPTED;
};

export const getLoggedOutMasteryMap = (
    tutorials: Array<{
        contentItems: Array<ContentItem>;
    }>,
): MasteryMap =>
    tutorials
        .map(({contentItems}) => getMasteryMapFromContentItems(contentItems))
        .reduce<
            Record<string, any>
        >((acc, tutorialMap) => Object.assign(acc, tutorialMap), {});

export const getContentItems = (
    tutorials: Array<QuizTutorial>,
): Array<ContentItem> =>
    tutorials.reduce<Array<any>>(
        (accum, tutorial) => [...accum, ...tutorial.contentItems],
        [],
    ) as any;

export const getAllExercises = (
    tutorials: Array<QuizTutorial>,
): Array<Exercise> =>
    getContentItems(tutorials).filter(({kind}) => kind === "Exercise") as any;

// TODO (jsatk): DRY up similarities between `getProficientExercises` and
// `getMasteredExercises`.
export const getProficientExercises = (
    tutorials: Array<QuizTutorial>,
    masteryMap: MasteryMap,
): Array<Exercise> =>
    getAllExercises(tutorials).filter(
        ({contentId}) => masteryMap[`e${contentId}`] === "proficient",
    ) as any;

export const getMasteredExercises = (
    tutorials: Array<QuizTutorial>,
    masteryMap: MasteryMap,
): Array<Exercise> =>
    getAllExercises(tutorials).filter(
        ({contentId}) => masteryMap[`e${contentId}`] === "mastered",
    ) as any;

export const getExerciseLevels = (
    tutorials: Array<QuizTutorial>,
    masteryMap: MasteryMap,
): Array<MasteryLevel> =>
    getAllExercises(tutorials).map(
        (exercise) => masteryMap[`e${exercise.contentId}`],
    ) as any;

/**
 * Reports the total number of mastery points the user has ever accumulated in
 * the skills represented by `tutorials`. Note that the result is a total over
 * all time, not the amount earned in a given session.
 */
export const getTotalMasteryPointsEarned = (
    tutorials: Array<QuizTutorial>,
    masteryMap: MasteryMap,
): number =>
    getExerciseLevels(tutorials, masteryMap).reduce(
        (sum, level: MasteryLevel) => sum + levelPointsMap[level],
        0,
    );

export const getTotalMasteryPointsAvailable = (
    tutorials: Array<QuizTutorial>,
    masteryMap: MasteryMap,
): number =>
    getExerciseLevels(tutorials, masteryMap).length *
    levelPointsMap[constants.MASTERED];

type ExerciseLevelChangesMap = {
    leveledUp: Array<Exercise>;
    leveledDown: Array<Exercise>;
    levelMaintained: Array<Exercise>;
};

export const getExerciseLevelChanges = (
    exercises: Array<Exercise>,
    oldMasteryMap: MasteryMap,
    newMasteryMap: MasteryMap,
): ExerciseLevelChangesMap => {
    const getLevelChangeKey = (exercise: Exercise): string => {
        const startLevel = getMasteryLevelForExerciseFromMap(
            exercise,
            oldMasteryMap,
        );
        const endLevel = getMasteryLevelForExerciseFromMap(
            exercise,
            newMasteryMap,
        );
        const levelChange = getLevelChange(startLevel, endLevel);

        if (levelChange > 0) {
            return "leveledUp";
        }
        if (levelChange < 0) {
            return "leveledDown";
        }
        return "levelMaintained";
    };

    return exercises.reduce(
        (levelChangesMap, exercise) => {
            const levelChangeKey = getLevelChangeKey(exercise);

            return {
                ...levelChangesMap,
                [levelChangeKey]: levelChangesMap[levelChangeKey].concat([
                    exercise,
                ]),
            };
        },
        {
            leveledUp: [],
            leveledDown: [],
            levelMaintained: [],
        },
    );
};

/**
 * Computes the number of mastery points the user has earned between two states
 * of their mastery maps, across a set of exercises.
 */
export const getMasteryPointChange = (
    exercises: ReadonlyArray<Exercise>,
    oldMasteryMap: Readonly<MasteryMap>,
    newMasteryMap: Readonly<MasteryMap>,
): number =>
    exercises.reduce((sum, exercise) => {
        const oldLevel = getMasteryLevelForExerciseFromMap(
            exercise,
            oldMasteryMap,
        );
        const newLevel = getMasteryLevelForExerciseFromMap(
            exercise,
            newMasteryMap,
        );

        return sum + levelPointsMap[newLevel] - levelPointsMap[oldLevel];
    }, 0);

export function getUpdatedMasteryPoints(
    masteryPoints: Readonly<MasteryPoints>,
    masteryMap: Readonly<MasteryMap> | null | undefined,
    promotions: Readonly<MasteryMap>,
): Readonly<MasteryPoints> {
    if (masteryPoints.pointsAvailable === 0) {
        return masteryPoints;
    }

    const oldMasteryMap: Readonly<MasteryMap> | null | undefined = masteryMap;
    const newMasteryMap: Readonly<MasteryMap> = {
        ...(masteryMap as any),
        ...(promotions as any),
    };

    const masteryKeys = Object.keys(newMasteryMap);

    if (masteryKeys.length === 0) {
        return masteryPoints;
    }

    const pointsEarned = masteryKeys.reduce((accum, masteryKey) => {
        // Ignore progressKeys which aren't in the original masteryMap.  These
        // are mostly likely "covered" items.  We ignore "covered" items
        // when computing unit and course (subject) level mastery.
        if (!oldMasteryMap?.[masteryKey]) {
            return accum;
        }
        const points = levelPointsMap[newMasteryMap[masteryKey]];
        return accum + points;
    }, 0);

    const pointsAvailable = masteryPoints.pointsAvailable;
    const percentage = getRoundedPercent(pointsEarned, pointsAvailable);

    return {
        percentage,
        pointsEarned,
        pointsAvailable,
    };
}

export const masteryColors = {
    unfamiliar: "#F7F8FA",
    attempted: "#C66024",
    familiar: "#D77D0F",
    proficient: "#948EBE",
    mastered: "#594094",
} as const;

export const masteryLevelOrder = [
    masteryValueConstants.ATTEMPTED,
    masteryValueConstants.FAMILIAR,
    masteryValueConstants.PROFICIENT,
    masteryValueConstants.MASTERED,
    masteryValueConstants.UNFAMILIAR,
] as const;

/**
 * getRemainingPointsAvailable returns the number of points still available to
 * be obtained by mastering all remaining skills in a given Quiz/UnitTest.
 */
export const getRemainingPointsAvailable = (
    quiz: InterspersedQuizT,
    masteryMap: MasteryMap,
): number => {
    const masteryLevels = getMasteryLevelsForQuizOrTest(quiz, masteryMap);
    // For each of the Exercises in a Quiz/UnitTest, we calculate the points
    // yet to be obtained and sum it to get the total points remaining.
    // 'Mastered' can't be achieved via quizzes, so we constrain the max
    // available mastery points for quizzes.
    const maxPts =
        quiz.type === "quiz"
            ? levelPointsMap[constants.PROFICIENT]
            : levelPointsMap[constants.MASTERED];
    return masteryLevels.reduce((remainingPts, masteryLevel) => {
        const obtainedPts = levelPointsMap[masteryLevel];
        return remainingPts + Math.max(0, maxPts - obtainedPts);
    }, 0);
};
